/**
 * work.js — pure helpers for the personal work list ("My Work") and the effort levers.
 *
 * PURE (no DOM, no engine state, no service imports) so it is unit-testable under plain node (see
 * work.test.mjs) and safe to import from both the outline engine and React.
 *
 * Since 2026-09-02 a person's work IS their crewed flights (docs/FLOW_RECONCILIATION_PLAN.md):
 * `normalizeFlight` turns a crew-index flight (frontend/src/outline/crew.js) into the row shape
 * the list ranks and renders. The RASCI-row normalizers that used to live here are gone with the
 * RASCI table itself. The due-date bucketing/ranking and the accomplishments math are unchanged.
 */

/* ── Effort levers (still used by Pulse's capacity panels) ──────────────────────────────────── */

// Rough, tunable per-item effort estimates (HOURS) for NON-milestone work, so threads, RFIs,
// sheets, and BIM count toward load even though they have no scheduler "Days Budgeted".
// Deliberately rough "to keep the team moving"; revisit once per-item effort is measured.
export const ASSIGN_EFFORT_HOURS = {
  'Thread': 1.5,            // covers RFIs / Submittals (Type='Thread')
  'Agency Review': 0.5,     // mostly waiting on the agency; small tracking effort on our side
  'Drawing Sheet': 6,
  'BIM System': 4,
}

// The above are DEFAULTS. The live values are tunable levers (scheduler_config, grp 'Effort',
// applies_to 'workload') so the hidden-load estimate can be adjusted without a code change, and so
// the calibration loop can eventually replace them with actuals. This map ties each lever key to its Type.
export const EFFORT_LEVER_KEYS = {
  effort_thread_hours: 'Thread',
  effort_agency_hours: 'Agency Review',
  effort_sheet_hours:  'Drawing Sheet',
  effort_bim_hours:    'BIM System',
}

/**
 * Build a Type→hours map from a flat lever map (leverMap output), falling back to ASSIGN_EFFORT_HOURS
 * for any lever not set.
 */
export function effortHoursFromLevers(levers) {
  const lv = levers || {}
  const out = { ...ASSIGN_EFFORT_HOURS }
  for (const [k, type] of Object.entries(EFFORT_LEVER_KEYS)) {
    const v = Number(lv[k])
    if (Number.isFinite(v) && v >= 0) out[type] = v
  }
  return out
}

/** Rough effort (in days) a non-milestone item of this Type adds to a person's load. */
export function assignmentEffortDays(type, overrideHours) {
  const map = overrideHours || ASSIGN_EFFORT_HOURS
  const h = map[type] ?? ASSIGN_EFFORT_HOURS[type]
  return h ? h / 8 : 0
}

/* ── Flights → work rows ────────────────────────────────────────────────────────────────────── */

function firstOf(v) { return Array.isArray(v) ? (v[0] ?? null) : (v ?? null) }
function dayStr(v) { const s = firstOf(v); return s ? String(s).slice(0, 10) : null }

// Where a plane is on the hill, in the board's own words (mirrors FlightTracker's zones).
export function hillStage(t) {
  const p = Number(t)
  if (!Number.isFinite(p)) return ''
  if (p < 0.15) return 'Wheels up'
  if (p < 0.5) return 'Plotting the course'
  if (p < 0.85) return 'Flying the course'
  if (p < 0.96) return 'On final approach'
  return 'Ready to land'
}

/**
 * Normalize one crew-index flight into the My Work row shape.
 * @param {object} flight  crew-index flight (see api/flights.js#crewIndex)
 * @param {string} memberId  the viewer — used to mark captain vs first officer
 */
export function normalizeFlight(flight, memberId) {
  const kind = flight.kind || 'milestone'
  const me = (flight.crew || []).find(c => c.memberAirtableId === memberId)
  const label = kind === 'squadron'
    ? ((flight.milestoneName || '').trim() || `Squadron · ${(flight.threads || []).length} threads`)
    : (flight.milestoneName || (kind === 'thread' ? 'Thread' : 'Milestone'))
  return {
    id:           flight.flightId,
    kind,
    typeLabel:    kind === 'squadron' ? 'Squadron' : kind === 'thread' ? 'Thread' : 'Milestone',
    flightNumber: flight.flightNumber || '',
    label,
    project:      flight.projectName || '',
    projectId:    flight.projectId || null,
    phaseName:    flight.phaseName || null,
    due:          kind === 'milestone' ? dayStr(flight.latestFinish) : null,
    urgency:      '',
    hillPosition: flight.hillPosition,
    stage:        hillStage(flight.hillPosition),
    isCaptain:    !!(me && me.isCaptain),
    priority:     me && (me.priority === 1 || me.priority === 2) ? me.priority : null,   // this person's rank for this plane (2026-09-10)
    crewNames:    (flight.crew || []).map(c => c.name).filter(Boolean),
    threads:      flight.threads || [],
    zoomType:     kind === 'milestone' ? 'milestone' : kind === 'thread' ? 'thread' : null,
    entityId:     kind === 'milestone' ? flight.milestoneId : kind === 'thread' ? flight.threadId : null,
  }
}

/* ── Due-date bucketing + ranking ───────────────────────────────────────────────────────────── */

const SOON_DAYS = 7

// Bucket ids — also the section order in the UI.
export const BUCKETS = [
  { key: 'overdue', label: 'Overdue' },
  { key: 'soon',    label: 'Due soon' },
  { key: 'later',   label: 'Later' },
  { key: 'nodate',  label: 'No date' },
]

function toDayInt(ymd) {
  // ymd: 'YYYY-MM-DD' → integer day number (UTC). NaN-safe.
  const ms = Date.parse(ymd + 'T00:00:00Z')
  return Number.isNaN(ms) ? null : Math.floor(ms / 86400000)
}

function urgencyBump(u) {
  if (!u) return 0
  const s = u.toUpperCase()
  if (s.includes('IMPOSSIBLE')) return -2
  if (s.includes('URGENT')) return -1
  return 0
}

/** Which bucket a normalized item falls in, relative to `todayYmd`. */
export function bucketOf(n, todayYmd) {
  if (!n.due) return 'nodate'
  const d = toDayInt(n.due), t = toDayInt(todayYmd)
  if (d == null || t == null) return 'nodate'
  if (d < t) return 'overdue'
  if (d <= t + SOON_DAYS) return 'soon'
  return 'later'
}

const KIND_ORDER = ['milestone', 'squadron', 'thread']

/**
 * Deterministic sort key (lower sorts first):
 *   [bucketIndex, withinBucketKey, urgencyBump, kindOrder, id]
 * - overdue: most-overdue first (key = -daysOverdue)
 * - soon/later: soonest first (key = due day int)
 */
export function scoreAssignment(n, todayYmd) {
  const bIdx = BUCKETS.findIndex(b => b.key === bucketOf(n, todayYmd))
  const t = toDayInt(todayYmd)
  const d = n.due ? toDayInt(n.due) : null
  let withinKey = 0
  if (d != null && t != null) withinKey = d < t ? -(t - d) : d
  const ki = KIND_ORDER.indexOf(n.kind)
  return [bIdx, withinKey, urgencyBump(n.urgency), ki < 0 ? 99 : ki, n.id]
}

function cmpTuple(a, b) {
  for (let i = 0; i < a.length; i++) {
    if (a[i] < b[i]) return -1
    if (a[i] > b[i]) return 1
  }
  return 0
}

/** Sort normalized items by the rank score. Returns a new array. */
export function rankAssignments(items, todayYmd) {
  return items
    .map(n => ({ n, s: scoreAssignment(n, todayYmd) }))
    .sort((a, b) => cmpTuple(a.s, b.s))
    .map(x => x.n)
}

/**
 * Already-normalized items → ranked, bucket-grouped work list.
 * @returns { items, groups: [{key,label,items}], counts: {overdue,soon,later,nodate,total} }
 */
export function groupNormalized(normItems, todayYmd) {
  const items = rankAssignments(normItems || [], todayYmd)
  const byBucket = { overdue: [], soon: [], later: [], nodate: [] }
  items.forEach(n => { byBucket[bucketOf(n, todayYmd)].push(n) })
  const groups = BUCKETS.filter(b => byBucket[b.key].length).map(b => ({ key: b.key, label: b.label, items: byBucket[b.key] }))
  const counts = {
    overdue: byBucket.overdue.length, soon: byBucket.soon.length,
    later: byBucket.later.length, nodate: byBucket.nodate.length, total: items.length,
  }
  return { items, groups, counts }
}

/** Today as 'YYYY-MM-DD' in local time (callers may inject their own for tests). */
export function isoToday(d = new Date()) {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/* ── Accomplishments (CT-025 Phase 5a — compute-only gamification) ───────────────────────────── */

function addDays(ymd, n) { return new Date(Date.parse(ymd + 'T00:00:00Z') + n * 86400000).toISOString().slice(0, 10) }
function isWeekend(ymd) { const d = new Date(ymd + 'T00:00:00Z').getUTCDay(); return d === 0 || d === 6 }
function prevWorkday(ymd) { let d = addDays(ymd, -1); while (isWeekend(d)) d = addDays(d, -1); return d }

/** Monday of the week containing `ymd`. */
export function startOfWeek(ymd) {
  const dow = new Date(ymd + 'T00:00:00Z').getUTCDay()   // 0 Sun .. 6 Sat
  return addDays(ymd, -(dow === 0 ? 6 : dow - 1))
}

/**
 * Current "don't break the chain" streak: consecutive WORKDAYS (Mon–Fri) with ≥1 completion, ending
 * at the most recent workday. Weekends never break it. If today is a workday with nothing done yet,
 * the streak counts back from the previous workday (the day isn't over — no penalty).
 * @param {Set<string>} daySet  set of YYYY-MM-DD days with a completion
 */
export function currentStreak(daySet, todayYmd) {
  let cur = todayYmd
  while (isWeekend(cur)) cur = addDays(cur, -1)     // weekend → step back to Friday
  if (!daySet.has(cur)) cur = prevWorkday(cur)      // today not done yet → don't penalize
  let streak = 0
  while (daySet.has(cur)) { streak++; cur = prevWorkday(cur) }
  return streak
}

/**
 * Compute the My Work accomplishments from completed checklist items + landed flights.
 * Pure. `checklistRecs` are Airtable checklist rows (`Completed Date`); `landings` are crew-index
 * landed entries ({ landedAt, latestFinish }) — an Airtable-shaped `{ fields: { 'Posted Date',
 * 'Latest Finish (LFT)' } }` row is accepted too, for the older callers.
 * @returns { weekItems, weekMilestones, weekOnTime, streak, onTimePct, onTimeCount, postedCount }
 */
export function computeAccomplishments(checklistRecs, landings, todayYmd) {
  const weekStart = startOfWeek(todayYmd)
  const days = new Set()
  let weekItems = 0
  ;(checklistRecs || []).forEach(r => {
    const f = r.fields || {}
    const d = dayStr(f['Completed Date'] || f['Day Completed'])
    if (!d) return
    days.add(d)
    if (d >= weekStart && d <= todayYmd) weekItems++
  })
  let postedCount = 0, onTimeCount = 0, weekMilestones = 0, weekOnTime = 0
  ;(landings || []).forEach(r => {
    const f = r.fields || {}
    const posted = dayStr(r.landedAt || f['Posted Date'])
    if (!posted) return
    postedCount++
    const lft = dayStr(r.latestFinish || f['Latest Finish (LFT)'])
    const onTime = lft ? posted <= lft : false
    if (onTime) onTimeCount++
    if (posted >= weekStart && posted <= todayYmd) { weekMilestones++; if (onTime) weekOnTime++ }
  })
  return {
    weekItems, weekMilestones, weekOnTime,
    streak: currentStreak(days, todayYmd),
    onTimePct: postedCount ? Math.round((onTimeCount / postedCount) * 100) : null,
    onTimeCount, postedCount,
  }
}

/** Compact due-date display, e.g. "Mar 4". */
export function fmtDueDate(ymd) {
  if (!ymd) return ''
  const ms = Date.parse(ymd + 'T00:00:00Z')
  if (Number.isNaN(ms)) return ''
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

/** Whole days between two YYYY-MM-DD (b - a). NaN-safe → 0. */
export function daysBetween(a, b) {
  const da = toDayInt(a), db = toDayInt(b)
  return (da == null || db == null) ? 0 : db - da
}

/**
 * Plain-language reason a normalized item ranks where it does — the priority logic, surfaced.
 * @returns {string} e.g. "Overdue 5 days · flagged URGENT"
 */
export function explainPriority(n, todayYmd) {
  const parts = []
  const b = bucketOf(n, todayYmd)
  if (b === 'overdue') {
    const d = -daysBetween(todayYmd, n.due)   // n.due < today → positive day count
    parts.push(`Overdue ${d} day${d === 1 ? '' : 's'}`)
  } else if (b === 'soon') {
    const d = daysBetween(todayYmd, n.due)
    parts.push(d === 0 ? 'Due today' : `Due in ${d} day${d === 1 ? '' : 's'}`)
  } else if (b === 'later') {
    parts.push('Due ' + fmtDueDate(n.due))
  } else {
    parts.push('No due date')
  }
  const u = (n.urgency || '').toUpperCase()
  if (u.includes('IMPOSSIBLE')) parts.push('flagged impossible by the scheduler')
  else if (u.includes('URGENT')) parts.push('flagged urgent by the scheduler')
  return parts.join(' · ')
}
